import React, { useState, useMemo } from "react";
import {
  CompanyResponse,
  PropsCompanyDetails,
} from "../../types/interfaces";
import { Lock, Unlock, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import unknownLogo from "../../assets/unknown_logo.png";
import damelioLogo from "../../assets/damelio_logo_negro.png";
import { useTranslation } from "react-i18next";

type Props = PropsCompanyDetails & { pageSize?: number };

const CompanyList: React.FC<Props> = ({ companies, onSelect, pageSize = 6 }) => {
  const { t } = useTranslation();
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [page, setPage] = useState(1);                    // ← página actual (1-based)

  /* ───────── helpers de paginación ───────── */
  const totalPages = Math.max(1, Math.ceil(companies.length / pageSize));

  // Memo para no recalcular en cada render
  const pagedCompanies = useMemo(() => {
    const start = (page - 1) * pageSize;
    return companies.slice(start, start + pageSize);
  }, [companies, page, pageSize]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const handleSelect = (company: CompanyResponse) => {
    if (!company.has_access) {
      setErrorMessage(t("company_list.no_access"));
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }
    setSelectedCompany(company.report_id.toString());
    onSelect(company.report_id.toString());
  };

  /* ───────── render ───────── */
  return (
    <div className="space-y-6">
      {errorMessage && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2" />
          {errorMessage}
        </div>
      )}

      {/* Tarjetas */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
        {pagedCompanies.map((company) => (
          <div
            key={company.report_id.toString()}
            onClick={() => handleSelect(company)}
            className={`relative bg-white shadow-lg rounded-lg p-6 cursor-pointer transition-all hover:shadow-2xl hover:-translate-y-1
              ${
                company.has_access
                  ? selectedCompany === company.report_id.toString()
                    ? "border-4 border-blue-600 bg-blue-50"
                    : "border-2 border-blue-500"
                  : "border border-gray-300 opacity-70"
              }`}
          >
            {/* icono lock/unlock */}
            <div className="absolute top-4 right-4 text-gray-600">
              {company.has_access ? (
                <Unlock className="w-6 h-6 text-green-600" />
              ) : (
                <Lock className="w-6 h-6 text-red-600" />
              )}
            </div>

            {/* logo */}
            <div className="flex justify-center mb-4">
              <img
                src={
                  company.logo_url === "damelio"
                    ? damelioLogo
                    : company.logo_url || unknownLogo
                }
                alt={`${company.name} logo`}
                className="w-16 h-16 object-cover rounded-full border"
                onError={(e) => (e.currentTarget.src = unknownLogo)}
              />
            </div>

            {/* nombre */}
            <h2 className="text-lg font-bold text-gray-800 text-center mb-4">
              {company.name}
            </h2>

            {/* métricas */}
            <div className="grid grid-cols-3 gap-2 text-center text-gray-700 mb-4">
              <Metric value={company.similar_in_city} label={t("company_list.same_city")} />
              <Metric value={company.similar_in_country} label={t("company_list.same_country")} />
              <Metric value={company.total_similar} label={t("company_list.total")} />
            </div>

            {/* dominio */}
            <p className="text-sm text-gray-600 text-center">
              {company.primary_domain
                ? `🌐 ${company.primary_domain}`
                : t("company_list.no_domain")}
            </p>
          </div>
        ))}
      </div>

      {/* Controles de paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-4 pt-2">
          <button
            onClick={goPrev}
            disabled={page === 1}
            className="p-2 rounded-full border disabled:opacity-40"
          >
            <ChevronLeft />
          </button>

          <span className="text-sm font-medium">
            {t("company_list.page")} {page} / {totalPages}
          </span>

          <button
            onClick={goNext}
            disabled={page === totalPages}
            className="p-2 rounded-full border disabled:opacity-40"
          >
            <ChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

/*  pequeño sub-componente para la métrica */
const Metric: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div>
    <span className="block font-semibold text-lg">{value}</span>
    <span className="text-sm">{label}</span>
  </div>
);

export default CompanyList;
